/**
 * audienceRules.js
 * ────────────────
 * Who may see a block. Lives in `block.style.audience` next to the device
 * visibility and is evaluated the same way by the editor (summary, badge)
 * and the public site (`Website/vue/visitor.js` supplies the visitor).
 *
 * Shape (every rule is AND-ed; empty lists mean "no restriction"):
 *   {
 *     login:      'any' | 'in' | 'out',
 *     membership: 'any' | 'member' | 'nonMember',   // member = at least one active membership
 *     membershipTypeIds: [],                        // with 'member': any of these types (empty = any type)
 *     newsletter: 'any' | 'subscribed' | 'notSubscribed',
 *     newsletterIds: [],                            // memberNewsletterIDs; with 'subscribed'/'notSubscribed' (empty = any)
 *     audienceIn: [],                               // visitor must be in at least one
 *     audienceNotIn: []                             // visitor must be in none
 *   }
 *
 * Visitor shape: { ready, loggedIn, membershipTypeIds, audienceIds, newsletterIds }.
 * A visitor that is not signed in has no memberships, newsletters or audiences.
 */

export var LOGIN_OPTIONS = [
	{ value: 'any', label: 'Everyone' },
	{ value: 'in', label: 'Signed in' },
	{ value: 'out', label: 'Signed out' }
];
export var MEMBERSHIP_OPTIONS = [
	{ value: 'any', label: 'Everyone' },
	{ value: 'member', label: 'Members' },
	{ value: 'nonMember', label: 'Non-members' }
];
export var NEWSLETTER_OPTIONS = [
	{ value: 'any', label: 'Everyone' },
	{ value: 'subscribed', label: 'Subscribers' },
	{ value: 'notSubscribed', label: 'Not subscribed' }
];

export function createDefaultAudience() {
	return { login: 'any', membership: 'any', membershipTypeIds: [], newsletter: 'any', newsletterIds: [], audienceIn: [], audienceNotIn: [] };
}

function ids(list) {
	if (!Array.isArray(list)) return [];
	return list.map(function (v) { return parseInt(v, 10); }).filter(function (n) { return !isNaN(n); });
}

function overlaps(a, b) {
	for (var i = 0; i < a.length; i++) if (b.indexOf(a[i]) !== -1) return true;
	return false;
}

/** True when the block has any audience restriction at all. */
export function hasAudienceRules(audience) {
	var a = audience || {};
	return (a.login && a.login !== 'any') || (a.membership && a.membership !== 'any') || (a.newsletter && a.newsletter !== 'any')
		|| ids(a.audienceIn).length > 0 || ids(a.audienceNotIn).length > 0;
}

/**
 * @param {Object|null} audience  block.style.audience
 * @param {Object|null} visitor   { loggedIn, membershipTypeIds, audienceIds, newsletterIds }
 * @returns {boolean}
 */
export function audienceAllows(audience, visitor) {
	var a = audience || {};
	if (!hasAudienceRules(a)) return true;
	var v = visitor || {};
	var loggedIn = !!v.loggedIn;
	var types = loggedIn ? ids(v.membershipTypeIds) : [];
	var auds = loggedIn ? ids(v.audienceIds) : [];
	var news = loggedIn ? ids(v.newsletterIds) : [];

	if (a.login === 'in' && !loggedIn) return false;
	if (a.login === 'out' && loggedIn) return false;

	if (a.membership === 'member' || a.membership === 'nonMember') {
		var want = ids(a.membershipTypeIds);
		var isMember = want.length ? overlaps(want, types) : types.length > 0;
		if (a.membership === 'member' && !isMember) return false;
		if (a.membership === 'nonMember' && isMember) return false;
	}

	if (a.newsletter === 'subscribed' || a.newsletter === 'notSubscribed') {
		var wantNews = ids(a.newsletterIds);
		var subscribed = wantNews.length ? overlaps(wantNews, news) : news.length > 0;
		if (a.newsletter === 'subscribed' && !subscribed) return false;
		if (a.newsletter === 'notSubscribed' && subscribed) return false;
	}

	var inList = ids(a.audienceIn);
	if (inList.length && !overlaps(inList, auds)) return false;
	var notIn = ids(a.audienceNotIn);
	if (notIn.length && overlaps(notIn, auds)) return false;
	return true;
}

/** Short human summary for card headers and canvas badges ('' when unrestricted). */
export function describeAudience(audience) {
	var a = audience || {};
	var parts = [];
	if (a.login === 'in') parts.push('Signed in');
	if (a.login === 'out') parts.push('Signed out');
	if (a.membership === 'member') parts.push(ids(a.membershipTypeIds).length ? 'Members (' + ids(a.membershipTypeIds).length + ' types)' : 'Members');
	if (a.membership === 'nonMember') parts.push('Non-members');
	if (a.newsletter === 'subscribed') parts.push('Subscribers');
	if (a.newsletter === 'notSubscribed') parts.push('Not subscribed');
	if (ids(a.audienceIn).length) parts.push('In ' + ids(a.audienceIn).length + ' audience' + (ids(a.audienceIn).length === 1 ? '' : 's'));
	if (ids(a.audienceNotIn).length) parts.push('Not in ' + ids(a.audienceNotIn).length);
	return parts.join(' · ');
}
